import { values } from "ramda";
import { type FC, memo, useContext, useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { Modal, TriggerButton, TriggerButtonGroup, TriggerButtonIntent, TriggerButtonSize } from "widgets";
import { useOverlay } from "widgets/hooks";

import style from "../styles/TrackHeader.module.scss";

import { DndActiveContext } from "../GenomeViewerInteractiveSurface.js";
import { TrackHeader } from "../TrackHeader.js";
import { type IConditionNameLookup } from "./FeatureTrack.js";
import FeatureTrackCustomizationDialog from "./FeatureTrackCustomizationDialog.js";
import { capitalizeFirstLetter, conditionallyPluralizeTrackLabel } from "@/app-utils/string.ts";
import { type IPileup } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import {
  type ISortableTrack,
  type ITrackDataMap,
  type ITrackIdentification,
  toggleShowAllTrackFeatures,
} from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { CANVAS_TRACK_MARGIN } from "@/settings/genome-viewer-settings.ts";
import { EXPRESSION_LANE_GAP, EXPRESSION_LANE_HEIGHT, TRACK_TYPE } from "@/settings/settings.ts";

export interface IFeatureTrackHeaderProps {
  track: ISortableTrack;
  pileup: IPileup;
  overflow: boolean;
  showAllFeatures: boolean;
  messageOnTopMargin?: number;
  isTrackHovered?: boolean;
}

const FeatureTrackHeader: FC<IFeatureTrackHeaderProps> = (props) => {
  const { pileup, showAllFeatures, overflow, track } = props;

  const showCustomizeButton = useMemo(
    () =>
      track.type === TRACK_TYPE.PROTEIN_LOCATION ||
      track.type === TRACK_TYPE.TRANSCRIPT_LOCATION ||
      track.type === TRACK_TYPE.GENE,
    [track],
  );

  const { triggerProps, overlayProps, state } = useOverlay({});

  const { setDndActive } = useContext(DndActiveContext);

  useEffect(() => {
    setDndActive(!state.isOpen);
  }, [setDndActive, state.isOpen]);

  const trackDataMap = useSelector<IAppState, ITrackDataMap>((state) => state.genomeBrowser.trackDataMap, shallowEqual);
  // extract the type of the current track if there are more tracks available with the same name
  const conditionalTrackType: string =
    values(trackDataMap).filter((v) => v.name === track.name).length > 1 ? track.type : null;

  return (
    <TrackHeader
      isTrackHovered={props.isTrackHovered}
      title={conditionallyPluralizeTrackLabel(track)}
      trackType={conditionalTrackType}
      trackIcons={
        <TriggerButtonGroup vertical>
          {showCustomizeButton && (
            <>
              <TriggerButton
                {...triggerProps}
                icon={"settings"}
                tooltip={`Customize ${conditionallyPluralizeTrackLabel(track)} track`}
                label={"Configure"}
                size={TriggerButtonSize.Small}
              />
              <Modal state={state} {...overlayProps}>
                <FeatureTrackCustomizationDialog trackType={track.type} onClose={state.close} />
              </Modal>
            </>
          )}

          <FeaturesLimitToggleButton
            track={track}
            trackSpec={track}
            pileup={pileup}
            overflow={overflow}
            showAllFeatures={showAllFeatures}
          />
        </TriggerButtonGroup>
      }
    >
      <ExpressionRowLabels
        track={track}
        pileup={pileup}
        overflow={overflow}
        messageOnTopMargin={props.messageOnTopMargin}
      />
    </TrackHeader>
  );
};

function getFeatureName(name: string, count: number): string {
  let ret = name.toLowerCase();
  if (name.includes(" ")) {
    ret = "feature";
  }
  return count > 1 ? ret + "s" : ret;
}

// --- Expression Row Labels ---
interface ExpressionRowLabelsProps {
  track: ISortableTrack;
  pileup: IPileup;
  overflow: boolean;
  messageOnTopMargin?: number;
}

export const ExpressionRowLabels: FC<ExpressionRowLabelsProps> = ({ pileup, track, overflow, messageOnTopMargin }) => {
  const conditionNameLookup = useSelector<IAppState, IConditionNameLookup>(
    (state) =>
      state.genomeBrowser.expressionColoringDatasets?.[track.trackCustomization?.expressionColoring?.datasetId]
        ?.conditionNamesLookup || {},
    shallowEqual,
  );

  // all the expression lanes should be the same, so we can get simply the ones for the first features a driver
  const { expressionParams } = { ...pileup?.features?.[0] };
  const labelsTop =
    EXPRESSION_LANE_GAP / 2 +
    (pileup?.pileupParams?.featureHeight ?? 0) +
    (pileup?.pileupParams?.labelHeight ?? 0) +
    CANVAS_TRACK_MARGIN +
    (messageOnTopMargin ?? 0);

  // don't show any labels unless there are some expression values to be shown
  if (expressionParams == undefined || overflow === true || pileup.expressionLaneNumber <= 0) {
    return null;
  }

  return (
    <div className={style.track_header_list} style={{ marginTop: labelsTop }}>
      {expressionParams.map((expr, index) => {
        const name = conditionNameLookup[expr.condition];
        return (
          <div
            key={`expression-label-${name || index}`}
            className={style.expression_row_label}
            style={{ lineHeight: `${EXPRESSION_LANE_HEIGHT}px` }}
          >
            {name}
          </div>
        );
      })}
    </div>
  );
};

interface IFeaturesLimitToggleButton {
  track: ISortableTrack;
  trackSpec: ITrackIdentification;
  pileup: IPileup;
  overflow: boolean;
  showAllFeatures: boolean;
}

export const FeaturesLimitToggleButton: FC<IFeaturesLimitToggleButton> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const { track, trackSpec, pileup, overflow, showAllFeatures } = props;
  const featureName = getFeatureName(track.name, pileup ? pileup.hiddenCount : 1);
  const featureNamePlural = getFeatureName(track.name, 2);
  const hiddenCount = pileup ? pileup.hiddenCount : 0;
  const totalCount = pileup?.features ? pileup.hiddenCount + pileup.features.length : 0;
  const showToggleButton = useMemo(() => {
    return (
      !!pileup &&
      !overflow &&
      (!!pileup.hiddenCount || showAllFeatures) &&
      !(pileup.lanes < pileup.pileupParams.maxLanes && showAllFeatures)
    );
  }, [pileup, showAllFeatures, overflow]);

  return (
    showToggleButton && (
      <TriggerButton
        icon={showAllFeatures ? "keyboard_double_arrow_up" : "keyboard_double_arrow_down"}
        intent={showAllFeatures ? TriggerButtonIntent.Active : TriggerButtonIntent.Secondary}
        tooltip={
          showAllFeatures
            ? `Show fewer ${featureNamePlural} by limiting the number of lanes in this track`
            : `Show hidden ${featureName} (${hiddenCount} of ${totalCount})`
        }
        label={
          showAllFeatures
            ? `Show Fewer ${capitalizeFirstLetter(featureNamePlural)}`
            : `Show Hidden ${capitalizeFirstLetter(featureName)}`
        }
        onPress={() => {
          void dispatch(toggleShowAllTrackFeatures(trackSpec));
        }}
        size={TriggerButtonSize.Small}
      />
    )
  );
};

/// --- Export --- ///
const MemoFeatureTrackHeader = memo(FeatureTrackHeader, shallowEqual);

export { MemoFeatureTrackHeader as FeatureTrackHeader };
